import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { getInspectionsList, type InspectionResponse } from '@/api/inspection.api'
import type { Status } from '@/types/general-types'
import { getErrorMessage } from '@/utils/utils'

type InspectionsState = {
  byDevice: Record<string, InspectionResponse[]>
  statusByDevice: Record<string, Status>
  rangeByDevice: Record<string, string>
}

const initialState: InspectionsState = {
  byDevice: {},
  statusByDevice: {},
  rangeByDevice: {},
}

export const rangeKey = (fromDate: string, toDate: string) => `${fromDate}|${toDate}`

export const getDeviceInspections = createAsyncThunk<
  { deviceId: string; range: string; inspections: InspectionResponse[] },
  { deviceId: string; fromDate: string; toDate: string },
  { rejectValue: string }
>('inspections/getDeviceInspections', async ({ deviceId, fromDate, toDate }, { rejectWithValue }) => {
  try {
    const inspections = await getInspectionsList({ deviceId, fromDate, toDate })

    return { deviceId, range: rangeKey(fromDate, toDate), inspections }
  } catch (error) {
    return rejectWithValue(getErrorMessage(error))
  }
})

const inspectionsSlice = createSlice({
  name: 'inspections',
  initialState,
  reducers: {
    inspectionConfirmed: (state, action: PayloadAction<{ deviceId: string; inspectionId: string }>) => {
      const list = state.byDevice[action.payload.deviceId]
      if (!list) return

      state.byDevice[action.payload.deviceId] = list.map((item) =>
        item.id === action.payload.inspectionId ? { ...item, isChecked: true } : item,
      )
    },
    inspectionRemoved: (state, action: PayloadAction<{ deviceId: string; inspectionId: string }>) => {
      const list = state.byDevice[action.payload.deviceId]
      if (!list) return

      state.byDevice[action.payload.deviceId] = list.filter((item) => item.id !== action.payload.inspectionId)
    },
    deviceInspectionsInvalidated: (state, action: PayloadAction<string>) => {
      delete state.byDevice[action.payload]
      delete state.rangeByDevice[action.payload]
      state.statusByDevice[action.payload] = 'idle'
    },
    resetStore: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDeviceInspections.pending, (state, action) => {
        state.statusByDevice[action.meta.arg.deviceId] = 'loading'
      })
      .addCase(getDeviceInspections.fulfilled, (state, action) => {
        const { deviceId, range, inspections } = action.payload

        state.byDevice[deviceId] = inspections
        state.rangeByDevice[deviceId] = range
        state.statusByDevice[deviceId] = 'succeeded'
      })
      .addCase(getDeviceInspections.rejected, (state, action) => {
        state.statusByDevice[action.meta.arg.deviceId] = 'failed'
      })
  },
})

export const { inspectionConfirmed, inspectionRemoved, deviceInspectionsInvalidated, resetStore } =
  inspectionsSlice.actions

export default inspectionsSlice.reducer
